import { ChangeDetectorRef, Component, Inject, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { of, take } from 'rxjs';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSort, MatSortable } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { QBAccountAbstract } from '../../responses/qbAllAccountsResponse';
import { AccountService } from '../../services/AccountService';
import { ClassAbstract } from '../../responses/qbAllClassesResponse';
import { DepartmentAbstract } from '../../responses/qbAllDepartmentsResponse';
import { MatTabChangeEvent } from '@angular/material/tabs';

@Component({ standalone:false,
  selector: 'app-qbaccounts-dialog',
  templateUrl: 'qbAccounts-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['qbAccounts-dialog.component.scss']
})

export class QBAccountsDialogComponent {
  @ViewChild('accountsSortRef') set accountsSortRef(ms: MatSort) {
    this.accountsSort = ms;
    if(this.accountsSort) this.activeAccountsSort = {active:ms.active, direction:ms.start};
    this.setDataSourceAttributes();
  }
  @ViewChild('classesSortRef') set classesSortRef(ms: MatSort) {
    this.classesSort = ms;
    if(this.classesSort) this.activeClassesSort = {active:ms.active, direction:ms.start};
    this.setDataSourceAttributes();
  }
  @ViewChild('departmentsSortRef') set departmentsSortRef(ms: MatSort) {
    this.departmentsSort = ms;
    if(this.departmentsSort) this.activeDepartmentsSort = {active:ms.active, direction:ms.start};
    this.setDataSourceAttributes();
  }
  
  public accountsSort?: MatSort;
  public classesSort?: MatSort;
  public departmentsSort?: MatSort;
  public title: string= "QBO";
  public confirmButtonText = "OK";

  public busyLoadingAccounts:boolean=false;
  public busyLoadingClasses:boolean=false;
  public busyLoadingDepartments:boolean=false;

  public activeAccountsSort?:{active:string, direction:string};
  public activeClassesSort?:{active:string, direction:string};
  public activeDepartmentsSort?:{active:string, direction:string};

  public accountsFilterValue='';
  public classesFilterValue='';
  public departmentsFilterValue='';

  public currentTabIndex:number=0;
    
  setDataSourceAttributes() {
    if(this._accountsDataSource){
      this.accountsSort?.sort(({ id: 'fqn', start: 'asc'}) as MatSortable);
      this._accountsDataSource.sort = this.accountsSort;
    }
    if(this._classesDataSource){
      this.classesSort?.sort(({ id: 'fqn', start: 'asc'}) as MatSortable);
      this._classesDataSource.sort = this.classesSort;
    }
    if(this._departmentsDataSource){
      this.departmentsSort?.sort(({ id: 'fqn', start: 'asc'}) as MatSortable);
      this._departmentsDataSource.sort = this.departmentsSort;
    }
  }

  private _accountsDataSource?: MatTableDataSource<QBAccountAbstract>;
  private _classesDataSource?: MatTableDataSource<ClassAbstract>;
  private _departmentsDataSource?: MatTableDataSource<DepartmentAbstract>;

  constructor(@Inject(MAT_DIALOG_DATA) private data: any, private dialogRef: MatDialogRef<QBAccountsDialogComponent>, private accService:AccountService, private cd:ChangeDetectorRef) {
    if(data){
        this.title= this.title + " of '" + data.clientName + "'";
        if(data.customerId && data.clientId){
          this.busyLoadingAccounts=true;
          this.accService.getQBAllAccounts(data.customerId,data.clientId).pipe(take(1)).subscribe(r=>{
            this.busyLoadingAccounts=false;
            if(r.success) this.setAccountData(r.accountAbstracts);
            this.cd.detectChanges();
          });
          this.busyLoadingClasses=true;
          this.accService.getQBAllClasses(data.customerId,data.clientId).pipe(take(1)).subscribe(r=>{
            this.busyLoadingClasses=false;
            if(r.success) this.setClassData(r.classAbstracts);
            this.cd.detectChanges();
          });
          this.busyLoadingDepartments=true;
          this.accService.getQBAllDepartments(data.customerId,data.clientId).pipe(take(1)).subscribe(r=>{
            this.busyLoadingDepartments=false;
            if(r.success) this.setDepartmentData(r.departmentAbstracts);
            this.cd.detectChanges();
          });
        }
    }
  }

  ngAfterViewInit(){
    this.setDataSourceAttributes();
    this.cd.detectChanges();
  }

  get displayedAccountColumns() {
    return ['name', 'acctNum', 'fqn', 'type'];
  }
  get displayedClassColumns() {
    return ['name', 'fqn'];
  }
  get displayedDepartmentColumns() {
    return ['name', 'fqn'];
  }

  get accountsDataSource() {
    return this._accountsDataSource;
  }

  get classesDataSource() {
    return this._classesDataSource;
  }

  get departmentsDataSource() {
    return this._departmentsDataSource;
  }

  get indentAccountName(){
    return this.activeAccountsSort?.active==undefined || (this.activeAccountsSort?.active=='name' && this.activeAccountsSort?.direction=='') || (this.activeAccountsSort?.active=='fqn' && this.activeAccountsSort?.direction!='desc') ;
  }

  get indentClassName(){
    return (this.activeClassesSort?.active=='name' && this.activeClassesSort?.direction=='') || (this.activeClassesSort?.active!='name' && this.activeClassesSort?.direction!='desc');
  }

  get indentDepartmentName(){
    return (this.activeDepartmentsSort?.active=='name' && this.activeDepartmentsSort?.direction=='') || (this.activeDepartmentsSort?.active!='name' && this.activeDepartmentsSort?.direction!='desc');
  }

  tabChanged(tabChangeEvent: MatTabChangeEvent){
    this.currentTabIndex=tabChangeEvent.index;
  }

  public accountSortChange($event:any){
    this.activeAccountsSort=$event;
  }
  public classSortChange($event:any){
    this.activeClassesSort=$event;
  }
  public departmentSortChange($event:any){
    this.activeDepartmentsSort=$event;
  }

  public applyAccountsFilter(event: Event) {
    this.accountsFilterValue = (event.target as HTMLInputElement).value.trim().toLowerCase();
    this._accountsDataSource!.filter = this.accountsFilterValue;
  }

  public applyClassesFilter(event: Event) {
    this.classesFilterValue = (event.target as HTMLInputElement).value.trim().toLowerCase();
    this._classesDataSource!.filter = this.classesFilterValue;
  }

  public applyDepartmentsFilter(event: Event) {
    this.departmentsFilterValue = (event.target as HTMLInputElement).value.trim().toLowerCase();
    this._departmentsDataSource!.filter = this.departmentsFilterValue;
  }

  private setAccountData(accountAbstracts:QBAccountAbstract[]){
    this._accountsDataSource = new MatTableDataSource(this.flattenAccountAbstracts(accountAbstracts,0));
    this._accountsDataSource.sort = this.accountsSort;
    this._accountsDataSource.sortingDataAccessor = (item, property) => {
      switch (property) {
        case 'name': return item.name; 
        case 'acctNum': return item.acctNum ?? '';
        case 'fqn': return item.fullyQualifiedName;
        case 'type': return item.accountType;
        default: return '';
      }
    };
  }

  private flattenAccountAbstracts(accountAbstracts:QBAccountAbstract[], hierarchyLevel:number){
    let flatAccountAbstracts:QBAccountAbstract[]=[];
    accountAbstracts.forEach(aa=>{
      aa.hierarchyLevel=hierarchyLevel;
      flatAccountAbstracts.push(aa);
      if(aa.subAccounts?.length>0){
        var nextHierarchyLevel=hierarchyLevel+1;
        let aaArray = this.flattenAccountAbstracts(aa.subAccounts, nextHierarchyLevel);
        flatAccountAbstracts=flatAccountAbstracts.concat(aaArray);
      }
    });
    return flatAccountAbstracts;
  }

  private setClassData(classAbstracts:ClassAbstract[]){
    this._classesDataSource = new MatTableDataSource(this.flattenClassAbstracts(classAbstracts,0));
    this._classesDataSource.sort = this.classesSort;
    this._classesDataSource.sortingDataAccessor = (item, property) => {
      switch (property) {
        case 'name': return item.name; 
        case 'fqn': return item.fullyQualifiedName;
        default: return '';
      }
    };
  }

  private flattenClassAbstracts(classAbstracts:ClassAbstract[], hierarchyLevel:number){
    let flatClassAbstracts:ClassAbstract[]=[];
    classAbstracts.forEach(ca=>{
      ca.hierarchyLevel=hierarchyLevel;
      flatClassAbstracts.push(ca);
      if(ca.subClasses?.length>0){
        var nextHierarchyLevel=hierarchyLevel+1;
        let caArray = this.flattenClassAbstracts(ca.subClasses, nextHierarchyLevel);
        flatClassAbstracts=flatClassAbstracts.concat(caArray);
      }
    });
    return flatClassAbstracts;
  }

  private setDepartmentData(departmentAbstracts:DepartmentAbstract[]){
    this._departmentsDataSource = new MatTableDataSource(this.flattenDepartmentAbstracts(departmentAbstracts,0));
    this._departmentsDataSource.sort = this.departmentsSort;
    this._departmentsDataSource.sortingDataAccessor = (item, property) => {
      switch (property) {
        case 'name': return item.name; 
        case 'fqn': return item.fullyQualifiedName;
        default: return '';
      }
    };
  }

  private flattenDepartmentAbstracts(departmentAbstracts:DepartmentAbstract[], hierarchyLevel:number){
    let flatDepartmentAbstracts:DepartmentAbstract[]=[];
    departmentAbstracts.forEach(da=>{
      da.hierarchyLevel=hierarchyLevel;
      flatDepartmentAbstracts.push(da);
      if(da.subDepartments?.length>0){
        var nextHierarchyLevel=hierarchyLevel+1;
        let daArray = this.flattenDepartmentAbstracts(da.subDepartments, nextHierarchyLevel);
        flatDepartmentAbstracts=flatDepartmentAbstracts.concat(daArray);
      }
    });
    return flatDepartmentAbstracts;
  }

  onConfirmClick(): void {
    this.dialogRef.close(true);
  }

}
